<script lang="ts">
  import { _ } from "svelte-i18n";
  import {
    getSizes,
    howToMeasure,
    howToMeasureRepresent,
    getRepresentSizes,
    reorderSizes,
    getGender,
    getMerrellSizes,
  } from "$lib/data/sizeGuide.data";
  import ClothingTypeSwitcher from "$lib/components/SizeGuide/ClothingTypeSwitcher/ClothingTypeSwitcher.svelte";
  import SizeTable from "$lib/components/SizeGuide/SizeTable/SizeTable.svelte";
  import Typography from "$lib/components/Typography/Typography.svelte";
  import Container from "$lib/components/Container/Container.svelte";
  import PageTitle from "$lib/components/PageTitle/PageTitle.svelte";
  import { processInlineLinks } from "$lib/utils/url";
  import TopSpacer from "$lib/components/TopSpacer/TopSpacer.svelte";
  import Divider from "$lib/components/Divider/Divider.svelte";
  import MeasuringGuide from "$lib/components/SizeGuide/MeasuringGuide/MeasuringGuide.svelte";

  import ClothingTypeOverlay from "$lib/components/SizeGuide/ClothingTypeOverlay/ClothingTypeOverlay.svelte";

  import { regionStore } from "$lib/stores/region";
  const regionInfo = regionStore();
  const region = $regionInfo.region;

  export let selectedType = $_("outerwear & tops");
  export let selectedSize: string;
  export let unit = "cm";
  export let gender = "men";
  export let bigAndTall = false;
  export let represent = false;
  export let merrell = false;

  export let modal = false;

  let showFilterOverlay = false;

  type Group = {
    title: string;
    subTypes?: string[];
  };

  type ClothingGroup = {
    gender: string;
    types: Group[];
  };

  const groups = [
    { title: $_("outerwear & tops") },
    { title: $_("other outerwear") },
    { title: $_("trousers & denim") },
    { title: $_("footwear") },
    { title: $_("accessories"), subTypes: [$_("belts"), $_("gloves")] },
  ];

  const merrellGroups = [{ title: $_("footwear") }];

  const representGroups = [
    { title: $_("hoodie") },
    { title: $_("t-shirt") },
    { title: $_("jacket") },
    { title: $_("trousers") },
    { title: $_("cargos") },
    { title: $_("sweater") },
    { title: $_("parka") },
  ];

  const clothingGroups: ClothingGroup[] = [
    {
      gender: "men",
      types: represent ? representGroups : groups,
    },
    {
      gender: "women",
      types: represent ? representGroups : groups,
    },
  ];

  const merrellClothingGroups: ClothingGroup[] = [
    {
      gender: "unisex",
      types: merrellGroups,
    },
  ];

  let selectedGender =
    clothingGroups.find(cg => cg.gender === gender) || clothingGroups[0];
  $: gender = selectedGender.gender;

  $: if (
    !selectedGender.types.some(type => type.title === selectedType) &&
    !selectedGender.types.some(type => type.subTypes?.includes(selectedType))
  ) {
    selectedType = selectedGender.types[0].title;
  }

  let viewportWidth: number;
  let isMobile = false;
  let mdWidth = 768;

  $: isMobile = viewportWidth < mdWidth;
</script>

<style lang="scss">
  .size-guide {
    padding-bottom: 0px;
    &--switcher {
      padding: 32px 0 0 0;
    }
    &--intro {
      margin-top: 16px;
      margin-bottom: 1.75rem;
    }
    &--container {
      display: flex;

      border-bottom: 1px solid $on-surface;
      @include breakpoint(md) {
        flex-direction: row;
      }
      flex-direction: column;
    }

    &--represent-bar {
      display: flex;
      justify-content: space-between;
      background-color: $on-surface;
      height: 48px;
      align-items: center;
    }
  }

  @include breakpoint(md) {
    .size-guide {
      &--title {
        padding: 24px 0 20px;
      }
    }
  }

  @include breakpoint(lg) {
    .size-guide {
      &--intro {
        max-width: 588px;
        margin: 0 0 44px;
      }
    }
  }
</style>

<svelte:window bind:innerWidth={viewportWidth} />

<div class="size-guide">
  <Container>
    {#if !modal}<TopSpacer />{/if}
    <div class="size-guide--title">
      <PageTitle
        title={`${merrell ? `Belstaff x Merrell 1TRL ` : ""}${$_(
          "Size Guide",
        )}`}
      />
    </div>
    <div class="size-guide--intro">
      <Typography variant="body1-alt" component="p">
        {#if represent || merrell}{@html processInlineLinks(
            $_("sizeGuide.intro.represent"),
          )}{:else}{@html processInlineLinks($_("sizeGuide.intro"))}{/if}
      </Typography>
    </div></Container
  >

  {#if showFilterOverlay}
    <div class="filter--overlay">
      <ClothingTypeOverlay
        bind:showFilterOverlay
        bind:selectedType
        bind:typeSelection={selectedGender.types}
      />
    </div>
  {/if}

  <div class="size-guide--switcher">
    {#if represent}
      <div class="size-guide--represent-bar">
        <Container>
          <Typography
            component="span"
            variant="button1"
            theme="surface-variant"
          >
            {selectedType}
          </Typography></Container
        >
      </div>
    {:else if merrell}
      <ClothingTypeSwitcher
        clothingGroups={merrellClothingGroups}
        selectedGender={merrellClothingGroups[0]}
        bind:selectedType
        bind:unit
        bind:showFilterOverlay
      />
    {:else}
      <ClothingTypeSwitcher
        {clothingGroups}
        bind:selectedGender
        bind:selectedType
        bind:unit
        bind:showFilterOverlay
      />
    {/if}
  </div>
  <div class="size-guide--container">
    <SizeTable
      sizeGuide={represent
        ? getRepresentSizes(selectedType)
        : merrell
          ? reorderSizes(
              getGender(gender),
              "UK",
              getMerrellSizes(selectedType, unit),
            )
          : reorderSizes(
              getGender(gender),
              "UK",
              getSizes(gender, selectedType, unit),
            )}
      bind:unit
      fullWidth={selectedType === $_("footwear")}
      {modal}
      {selectedSize}
      bind:bigAndTall
    >
      {#if !isMobile}
        <MeasuringGuide
          bind:selectedType
          howToMeasureInfo={represent
            ? []
            : howToMeasure(gender)[selectedType]?.info}
          image={represent
            ? howToMeasureRepresent()[selectedType]?.image
            : howToMeasure(gender)[selectedType]?.image}
        />
      {/if}
    </SizeTable>

    {#if isMobile}
      <MeasuringGuide
        bind:selectedType
        howToMeasureInfo={represent
          ? []
          : howToMeasure(gender)[selectedType]?.info}
        image={represent
          ? howToMeasureRepresent()[selectedType]?.image
          : howToMeasure(gender)[selectedType]?.image}
      />
    {/if}
  </div>
</div>
